import { Box, Button, Flex, FormControl, FormErrorMessage, FormLabel, Heading, Input, Text } from "@chakra-ui/react";
import { Field, Formik } from "formik";
import { FC, ReactElement, useState } from "react";
import { FaArrowCircleRight } from "react-icons/fa";

interface CheckoutCustomerStepProps {
    handleSubmition: any;
    defaultFormValue: any;
}

const allowedDomains = [
    "gmail.com", "outlook.com", "yahoo.com", "yahoo.co.id", "hotmail.com",
    "icloud.com", "aol.com", "live.com", "gmx.com", "mail.com", "protonmail.com"
];

/** =============================
 *  Konfigurasi panjang nomor
 *  ============================= */
const PHONE_MIN = 8;
const PHONE_MAX = 17; // Indonesia: izinkan extended length (mis. >15)

/** =============================
 *  Utils: normalisasi & format
 *  ============================= */
function normalizePhone(raw: string): { e164: string; error?: string } {
    let s = (raw || "").trim();
    if (!s) return { e164: "", error: "Silahkan isi Nomor WhatsApp Anda." };

    // hapus spasi/tanda
    s = s.replace(/[\s\-().]/g, "");
    // 00 -> +
    if (s.startsWith("00")) s = "+" + s.slice(2);

    // +<digits> (internasional)
    if (s.startsWith("+")) {
        const digits = s.slice(1);
        if (!new RegExp(`^\\d{${PHONE_MIN},${PHONE_MAX}}$`).test(digits)) {
            return { e164: "", error: `Nomor WhatsApp tidak valid (${PHONE_MIN}–${PHONE_MAX} digit).` };
        }
        return { e164: "+" + digits };
    }

    // Indonesia 62... / 0...
    if (s.startsWith("62")) {
        const local = s.slice(2).replace(/^0+/, "");
        const fullDigits = "62" + local;
        if (!new RegExp(`^\\d{${PHONE_MIN},${PHONE_MAX}}$`).test(fullDigits)) {
            return { e164: "", error: `Nomor WhatsApp Indonesia tidak valid (${PHONE_MIN}–${PHONE_MAX} digit).` };
        }
        return { e164: "+" + fullDigits };
    }
    if (s.startsWith("0")) {
        const local = s.slice(1);
        const fullDigits = "62" + local;
        if (!new RegExp(`^\\d{${PHONE_MIN},${PHONE_MAX}}$`).test(fullDigits)) {
            return { e164: "", error: `Nomor WhatsApp Indonesia tidak valid (${PHONE_MIN}–${PHONE_MAX} digit).` };
        }
        return { e164: "+" + fullDigits };
    }

    // angka polos dianggap internasional (punya kode negara)
    if (new RegExp(`^\\d{${PHONE_MIN},${PHONE_MAX}}$`).test(s)) return { e164: "+" + s };

    return { e164: "", error: "Nomor WhatsApp tidak valid." };
}

// Auto-format tampilan saat ketik (Indonesia saja)
function formatPhoneDisplay(input: string): string {
    if (!input) return "";

    // bersihkan kecuali + dan digit
    const keepPlus = input.trim().startsWith("+");
    let s = input.replace(/[^\d+]/g, "");

    // Internasional selain +62 → biarkan apa adanya (tidak inject -/())
    if (keepPlus && !s.startsWith("+62")) return s;

    // 62... → tampilkan sebagai lokal 0...
    if (s.startsWith("+62")) s = "0" + s.slice(3);
    else if (s.startsWith("62")) s = "0" + s.slice(2);

    // Bukan Indonesia atau bukan 0 diawali → kembalikan apa adanya
    if (!s.startsWith("0")) return s;

    // Mobile: 08xx... → chunk dinamis 4-4-4-... (tidak terpotong)
    if (s[1] === "8") {
        const parts: string[] = [];
        // head 4 digit (0812)
        parts.push(s.slice(0, 4));
        // sisanya per 4 digit
        let rest = s.slice(4);
        while (rest.length > 0) {
            parts.push(rest.slice(0, 4));
            rest = rest.slice(4);
        }
        return parts.filter(Boolean).join("-");
    }

    // Landline: (area) number  — heuristik area
    const areaLen = s.startsWith("021") ? 3 : Math.min(4, s.length); // 021 → 3, lain → 4 (default)
    if (s.length <= areaLen) {
        // belum cukup digit → tampilkan "(" tanpa tutup biar user paham sedang isi kode area
        return `(${s}`;
    }
    const area = s.slice(0, areaLen);
    const rest = s.slice(areaLen);
    return `(${area}) ${rest}`;
}

const CheckoutCustomerStep: FC<CheckoutCustomerStepProps> = ({ handleSubmition, defaultFormValue }): ReactElement => {
    const [formValue] = useState<any>(defaultFormValue);
    // Loader lokal untuk tombol (meng-cover kasus navigate cepat)
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onSubmit = async (values: any, actions: any) => {
        setIsSubmitting(true);
        try {
            const { e164, error } = normalizePhone(values.phone);
            if (error) {
                actions.setFieldError("phone", error);
                return;
            }
            const payload = { ...values, phone: e164 };
            await handleSubmition(payload); // biasanya navigate di sini
        } catch (err) {
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Formik initialValues={formValue} onSubmit={onSubmit}>
            {({ handleSubmit, errors, touched, setFieldError, setFieldValue, values }: any) => {
                const loading = isSubmitting;
                return (
                    <form onSubmit={handleSubmit} noValidate>
                        <Box mt={{ base: 2, md: 0 }} mb={{ base: 2, md: 6 }} p={{ base: 2, md: 6 }} borderRadius={8} borderStyle={"solid"} borderWidth={{ base: 0, md: 1 }} borderColor={"blackAlpha.300"} textAlign={"center"}>
                            <Heading fontSize="lg" textAlign={{ base: "left", md: "center" }}>Isi Informasi Anda</Heading>
                            <Text fontSize={"xs"} textAlign={{ base: "left", md: "center" }} mt={2}>
                                Lengkapi informasi Anda di bawah ini. Pastikan email dan nomor WhatsApp benar.
                            </Text>

                            {/* Nama */}
                            <Box pt={8} pb={0}>
                                <FormControl isRequired isInvalid={!!(errors.name && touched.name)} isDisabled={loading}>
                                    <FormLabel htmlFor="name" fontSize={"sm"}>Nama Anda</FormLabel>
                                    <Field
                                        as={Input}
                                        id="name"
                                        name="name"
                                        placeholder="Masukan Nama Anda"
                                        validate={(v: any) => (!v || v.trim().length === 0 ? "Silahkan isi Nama Anda." : undefined)}
                                    />
                                    <FormErrorMessage>{errors.name}</FormErrorMessage>
                                    <Text textAlign={'left'} fontSize="xs" color="gray.500" mt={1}>
                                        Sesuai KTP/akun bank untuk kemudahan verifikasi.
                                    </Text>
                                </FormControl>
                            </Box>

                            {/* Email */}
                            <Box py={4} pb={0}>
                                <FormControl isRequired isInvalid={!!(errors.email && touched.email)} isDisabled={loading}>
                                    <FormLabel htmlFor="email" fontSize={"sm"}>Email Terbaik Anda</FormLabel>
                                    <Field
                                        as={Input}
                                        id="email"
                                        name="email"
                                        placeholder="Masukan Email Anda"
                                        validate={(value: any) => {
                                            if (!value || value.trim().length === 0) return "Silahkan isi Email Terbaik Anda.";
                                            if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,6}$/i.test(value)) return "Email Anda tidak valid.";
                                            const parts = value.split("@");
                                            if (parts.length !== 2) return "Email Anda tidak valid.";
                                            const domain = (parts[1] || "").toLowerCase();
                                            if (!allowedDomains.includes(domain)) return "Email dengan domain " + domain + " tidak diperbolehkan.";
                                            return undefined;
                                        }}
                                    />
                                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                                    <Text textAlign={'left'} fontSize="xs" color="gray.500" mt={1}>
                                        Akses Program akan dikirim otomatis ke email ini.
                                    </Text>
                                </FormControl>
                            </Box>

                            {/* WhatsApp */}
                            <Box pt={4}>
                                <FormControl isRequired isInvalid={!!(errors.phone && touched.phone)} isDisabled={loading}>
                                    <FormLabel htmlFor="phone" fontSize={"sm"}>Nomor WhatsApp Anda</FormLabel>
                                    <Field
                                        name="phone"
                                        validate={(val: string) => {
                                            const { error } = normalizePhone((val || "").trim());
                                            return error;
                                        }}
                                    >
                                        {({ field, form }: any) => (
                                            <Input
                                                id="phone"
                                                inputMode="tel"
                                                placeholder={`Masukan No. WhatsApp Aktif Anda`}
                                                value={formatPhoneDisplay(field.value || "")}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    const cleaned = raw.replace(/[^\d+]/g, "");
                                                    form.setFieldValue("phone", cleaned, false);
                                                    if (form.errors.phone) form.setFieldError("phone", undefined);
                                                }}
                                                onBlur={(e) => {
                                                    field.onBlur(e);
                                                    const { error } = normalizePhone(form.values.phone || "");
                                                    form.setFieldError("phone", error || undefined);
                                                }}
                                            />
                                        )}
                                    </Field>
                                    <FormErrorMessage>{errors.phone}</FormErrorMessage>
                                    <Text textAlign="left" fontSize="xs" color="gray.500" mt={1}>
                                        Gunakan No. Whatsapp Aktif agar pesan otomatis terkirim.
                                    </Text>
                                </FormControl>
                            </Box>
                        </Box>

                        <Flex width="100%" justify="center" mb={6}>
                            <Button type="submit" size={"lg"} rightIcon={<FaArrowCircleRight />} isLoading={loading}>
                                Lanjut ke Pembayaran
                            </Button>
                        </Flex>
                    </form>
                );
            }}
        </Formik>
    );
};

export default CheckoutCustomerStep;
